// apps/client/src/components/reports/DataAvailabilityBanner.tsx
// Renders the per-template data-availability matrix on /reports/approved-vs-settled.
// Banner is informational when SOME templates support variance, blocking when none do.

import React from 'react';
import { AlertTriangle, CheckCircle2, MinusCircle } from 'lucide-react';
import type { PerTemplateAvailability } from '../../hooks/reports/useApprovedVsSettledVariance';

interface DataAvailabilityBannerProps {
  perTemplate: PerTemplateAvailability[];
  overallSupportsVariance: boolean;
  /** Map of loanBookId / templateId → display name. */
  loanBookNames?: Record<string, string>;
}

export const DataAvailabilityBanner: React.FC<DataAvailabilityBannerProps> = ({
  perTemplate,
  overallSupportsVariance,
  loanBookNames = {},
}) => {
  if (perTemplate.length === 0) {
    return null;
  }

  const hasNoSignalLoanBooks = perTemplate.some(t => !t.supportsVariance && t.upfrontRows > 0);

  // Hide the banner entirely if every active loan book supports variance — no need to nag.
  if (overallSupportsVariance && !hasNoSignalLoanBooks) {
    return null;
  }

  return (
    <div
      className={`rounded-lg border ${overallSupportsVariance
        ? 'bg-amber-500/5 border-amber-500/30'
        : 'bg-red-500/5 border-red-500/30'} p-4`}
    >
      <div className="flex items-start gap-3">
        <AlertTriangle
          className={`w-5 h-5 mt-0.5 flex-shrink-0 ${overallSupportsVariance ? 'text-amber-400' : 'text-red-400'}`}
        />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-white">
            {overallSupportsVariance
              ? 'Some loan books are missing approved-amount data'
              : 'Variance reporting is not yet available for this scope'}
          </h3>
          <p className="text-sm text-gray-400 mt-1 leading-relaxed">
            {overallSupportsVariance
              ? "We compare the approved amount against the settled amount on each loan. The loan books below don't yet capture the approved amount in their commission file — once that column is added the rows will start showing."
              : "None of the loan books in this scope capture the approved amount yet. Add the lender's approved-amount or balance column to the loan book's commission-file settings to start measuring variance."}
          </p>

          <div className="mt-3 space-y-1.5">
            {perTemplate.map(t => {
              const name = loanBookNames[t.templateId] || t.templateId;
              const pct = (t.pctWithSignal * 100).toFixed(0);
              return (
                <div
                  key={t.templateId}
                  className="flex items-center justify-between gap-3 text-xs py-1.5 px-2.5 rounded bg-black/20"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {t.supportsVariance ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                    ) : (
                      <MinusCircle className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                    )}
                    <span className="text-gray-200 truncate">{name}</span>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-gray-500">{t.upfrontRows.toLocaleString()} loans</span>
                    <span className={t.supportsVariance ? 'text-emerald-400' : 'text-gray-500'}>
                      {t.supportsVariance ? `${pct}% with approved amount` : 'no approved amount'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataAvailabilityBanner;
